import { IUserSetting } from "@utils/Interfaces";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUser } from "./UserContext";
import UserApi from "services/UserApi";
import Helpers from "@utils/Helpers";
import convert from "convert-units";

interface UnitContextType {
  units: any;
  setUnits: (newUnits: any) => void;
  updateUnit: (newUnits: any) => void;
  convertDistance: (distance: number) => any;
  convertCurrency: (amount: any, prevUnit: string, targetUnit: string) => any;
  convertWeight: (weight: any, prevUnit: string, targetUnit: string) => any;
  convertToSI: (weight: number, currentUnit: string) => any;
}

const UnitContext = createContext<UnitContextType | undefined>(undefined);

export const UnitProvider = ({ children }: { children: ReactNode }) => {
  const { userProfile, updateUserProfile } = useUser();
  const [units, setUnits] = useState<IUserSetting>(
    userProfile?.settings || {
      currency: "CAD",
      language: "en",
      distance: "km",
      length: "ft",
      weight: "kg",
    }
  );

  const updateUnit = async (data: any) => {
    setUnits(data);
    try {
      const response = await UserApi.changeLocalization(data);
      if (response.status === 200) {
        const access = response.data.data.access_token;
        const refresh = response.data.data.refresh_token;
        const userParsed = Helpers.decodeToken(access);
        setUnits(userParsed.user.settings);
        updateUserProfile(access, refresh);
      }
    } catch (error) {}
  };

  const prevUnitsRef: any = useRef(null);

  useEffect(() => {
    prevUnitsRef.current = units;
  }, [units]);

  const convertDistance = useCallback(
    (distance: number) => {
      let currentDistance = 0;
      switch (units.distance) {
        case "mi":
          currentDistance = distance * 0.621371;
          break;
        default:
          currentDistance = distance;
          break;
      }
      return currentDistance;
    },
    [units.distance]
  );

  const convertCurrency = useCallback(
    (amount: any, prev: string, target: string) => {
      if (prev === target) return amount;

      const USD_TO_CAD = 1.37;
      if (prev === "USD") {
        amount = Number(amount) * USD_TO_CAD;
      } else {
        amount = Number(amount) * (1 / USD_TO_CAD);
      }
      return amount;
    },
    []
  );

  const convertToSI = (weight: any, currentUnit: string) => {
    weight = Number(weight);
    let currentWeight = 0;
    switch (currentUnit) {
      case "lb":
        currentWeight = weight * 0.453592;
        break;
      case "ton":
        currentWeight = weight * 1000;
        break;
      default:
        currentWeight = weight;
        break;
    }
    return currentWeight;
  };

  const convertWeight = (weight: any, prevUnit: string, targetUnit: string) => {
    weight = Number(weight);
    prevUnit = prevUnit == "t" ? "mt" : prevUnit;
    targetUnit = targetUnit == "t" ? "mt" : targetUnit;

    const currentWeight = convert(weight).from(prevUnit).to(targetUnit);

    return currentWeight;
  };

  return (
    <UnitContext.Provider
      value={{
        units,
        setUnits,
        updateUnit,
        convertDistance,
        convertCurrency,
        convertWeight,
        convertToSI,
      }}
    >
      {children}
    </UnitContext.Provider>
  );
};

export const useUnit = () => {
  const context = useContext(UnitContext);
  if (context === undefined) {
    throw new Error("useUnit must be used within a UnitProvider");
  }
  return context;
};
